import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchTransactions } from '../redux/actions/thunks/Storage';
import LoadingIcon from './LoadingIcon'; // Import the loading icon component

const TransactionWindow = ({ onClose }) => {
  const transactions = useSelector(state => state.storage.transactions);
  const loading = useSelector(state => state.storage.loading);
  const dispatch = useDispatch(); // Initialize dispatch

  const [startDateInput, setStartDateInput] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });

  const [endDateInput, setEndDateInput] = useState(() => new Date().toISOString().split('T')[0]);

  const [startDate, setStartDate] = useState(startDateInput);
  const [endDate, setEndDate] = useState(endDateInput);
  const [searchTerm, setSearchTerm] = useState('');
  const hasPendingDateChange = startDateInput !== startDate || endDateInput !== endDate;

  const finnishMonths = [
    'Tammikuuta',
    'Helmikuuta',
    'Maaliskuuta',
    'Huhtikuuta',
    'Toukokuuta',
    'Kesäkuuta',
    'Heinäkuuta',
    'Elokuuta',
    'Syyskuuta',
    'Lokakuuta',
    'Marraskuuta',
    'Joulukuuta',
  ];

  const formatDateFinnish = (value) => {
    if (!value) {
      return '';
    }

    const date = new Date(`${value}T00:00:00`);
    const monthName = finnishMonths[date.getMonth()] || '';

    return `${date.getDate()}. ${monthName} ${date.getFullYear()}`;
  };

  const formatAmount = (amount, action) => {
    let amountClassName = '';
    let formattedAmount = '';

    if (action === '') {
      amountClassName = amount < 0 ? 'negative' : 'positive';
      formattedAmount = `${amount < 0 ? '-' : '+'}${Math.abs(amount)} kpl`;
    } else {
      amountClassName = 'default';
      formattedAmount = `${amount} kpl`;
    }

    return <span className={`Transaction-Amount ${amountClassName}`}>{formattedAmount}</span>;
  };

  const invalidRange = useMemo(
    () => !startDateInput || !endDateInput || new Date(startDateInput) > new Date(endDateInput),
    [endDateInput, startDateInput],
  );

  const applyDateRange = () => {
    if (!invalidRange) {
      setStartDate(startDateInput);
      setEndDate(endDateInput);
    }
  };

  useEffect(() => {
    if (startDate && endDate && new Date(startDate) <= new Date(endDate)) {
      dispatch(fetchTransactions({ startDate, endDate }));
    }
  }, [dispatch, endDate, startDate]);

  const transactionsToShow = useMemo(() => {
    if (invalidRange) {
      return [];
    }

    const normalizedSearch = searchTerm.toLowerCase();

    const itemToString = (item) => {
      if (item.isOther) {
        return `${item.item} ${item.info}`.trim();
      }

      if (item.isHandRail) {
        return `${item.handRail_woodType} ${item.model} ${item.length}mm`;
      }

      return `${item.woodType} ${item.length}x${item.depth}x${item.thickness}mm`;
    };

    const matchesSearch = (transaction) => {
      if (!normalizedSearch) {
        return true;
      }

      const combinedText = [
        transaction.person,
        transaction.action,
        itemToString(transaction.item),
        transaction.oldItem ? itemToString(transaction.oldItem) : '',
      ]
        .join(' ')
        .toLowerCase();

      return combinedText.includes(normalizedSearch);
    };

    return transactions.filter(matchesSearch);
  }, [invalidRange, searchTerm, transactions]);

  const formattedDateRange = useMemo(
    () => `${formatDateFinnish(startDate)} - ${formatDateFinnish(endDate)}`,
    [endDate, startDate],
  );

  return (
    <div className="Transaction-Overlay">
      <div className="Transaction-Container Summary-Container">
        <div className="Transaction-Header">
          <h2 className="Transaction-Title">Tapahtumat</h2>
          <button className="Transaction-CloseButton" onClick={onClose}>
            Sulje
          </button>
        </div>

        <div className="Summary-Filters">
          <div className="Summary-DateInput">
            <label htmlFor="transaction-start-date">Alkaen</label>
            <input
              id="transaction-start-date"
              type="date"
              value={startDateInput}
              onChange={(event) => setStartDateInput(event.target.value)}
            />
          </div>
          <div className="Summary-DateInput">
            <label htmlFor="transaction-end-date">Päättyen</label>
            <input
              id="transaction-end-date"
              type="date"
              value={endDateInput}
              onChange={(event) => setEndDateInput(event.target.value)}
            />
          </div>
          <div className="Summary-ControlGroup">
            <label htmlFor="transaction-search">Haku</label>
            <input
              id="transaction-search"
              type="text"
              value={searchTerm}
              placeholder="Etsi henkilöä tai tuotetta"
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <button className="Summary-ApplyButton" onClick={applyDateRange} disabled={invalidRange}>
            Hae
          </button>
        </div>

        <div className="Transaction-ListContainer Summary-ListContainer">
          {loading ? (
            <div className="Summary-ListPlaceholder">
              <LoadingIcon />
            </div>
          ) : (
            <>
              {invalidRange ? (
                <p className="Summary-Empty">
                  Aloituspäivän tulee olla ennen lopetuspäivää.
                </p>
              ) : hasPendingDateChange ? (
                <p className="Summary-Empty">Klikkaa "Hae" päivittääksesi tulokset.</p>
              ) : (
                <>
                  <div className="Summary-Totals">
                    <span>Ajanjakso: {formattedDateRange}</span>
                  </div>

                  {transactionsToShow.length === 0 ? (
                    <p className="Summary-Empty">Ei tapahtumia valitulla aikavälillä.</p>
                  ) : (
                    <ul className="Transaction-List">
                      {transactionsToShow.map((transaction) => (
                        <li className="Transaction-Item" key={transaction._id}>
                          <div className="Transaction-Info">
                            <span className="Transaction-Person">{transaction.person}:</span>
                            <span className="Transaction-ItemDetails">
                              {transaction.item.isOther ? (
                                <>
                                  {transaction.item.item} {transaction.item.info}
                                </>
                              ) : transaction.item.isHandRail ? (
                                <>
                                  {transaction.item.handRail_woodType} {transaction.item.model},{' '}
                                  {transaction.item.length}mm
                                </>
                              ) : (
                                <>
                                  {transaction.item.woodType}{' '}
                                  {transaction.item.length}x{transaction.item.depth}x
                                  {transaction.item.thickness}mm
                                </>
                              )}
                            </span>
                            <span className="Transaction-Amount">
                              {formatAmount(transaction.amount, transaction.action)}
                            </span>
                            <span className="Transaction-Time">
                              ({new Date(transaction.time).toLocaleString('fi-FI')})
                            </span>
                            {transaction.action && (
                              <span
                                className={`Transaction-Action ${
                                  transaction.action === 'Poistettu'
                                    ? 'Transaction-ActionRemove'
                                    : transaction.action === 'Uusi'
                                    ? 'Transaction-ActionAdd'
                                    : transaction.action === 'Muokattu'
                                    ? 'Transaction-ActionModified'
                                    : ''
                                }`}
                              >
                                {transaction.action}
                              </span>
                            )}
                          </div>

                          {transaction.action === 'Muokattu' && (
                            <div className="Transaction-OldItemInfo">
                              <span className="Transaction-OldItemDetails">
                                {'Vanha: '}
                                {transaction.oldItem.isOther ? (
                                  <>
                                    {transaction.oldItem.item} {transaction.oldItem.info}
                                  </>
                                ) : transaction.oldItem.isHandRail ? (
                                  <>
                                    {transaction.oldItem.handRail_woodType} {transaction.oldItem.model},{' '}
                                    {transaction.oldItem.length}mm
                                  </>
                                ) : (
                                  <>
                                    {transaction.oldItem.woodType}{' '}
                                    {transaction.oldItem.length}x{transaction.oldItem.depth}x
                                    {transaction.oldItem.thickness}mm
                                  </>
                                )}
                              </span>
                              <span className="Transaction-OldAmount">
                                {formatAmount(transaction.oldItem.amount)}
                              </span>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionWindow;
